import { useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { z } from 'zod';
import { Button, Card, CardContent } from '@lombok/ui';
import { Logo } from '@/components/Logo';
import { rifSchema } from '@lombok/shared';

/**
 * Public account-request form. Onboarding in B2B is request → admin approval
 * (docs + credit review happen in the app afterward), so this only captures
 * intent. It is intentionally not wired to the API in the scaffold; on submit
 * it shows a Spanish confirmation. Validation reuses the same Zod + RHF stack.
 */
const requestSchema = z.object({
  accountType: z.enum(['cliente', 'distribuidor'], {
    errorMap: () => ({ message: 'Selecciona un tipo de cuenta' }),
  }),
  companyName: z.string().min(2, 'Ingresa el nombre de la empresa').max(160),
  rif: rifSchema,
  contactName: z.string().min(2, 'Ingresa un nombre de contacto').max(120),
  email: z.string().min(1, 'Ingresa un correo').email('Correo no válido'),
  phone: z.string().min(7, 'Ingresa un teléfono válido').max(30),
  message: z.string().max(1000).optional(),
});
type RequestValues = z.infer<typeof requestSchema>;

export function RequestAccountPage() {
  const [submitted, setSubmitted] = useState(false);
  const reduced = useReducedMotion();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: { accountType: 'cliente' },
  });

  const onSubmit = () => {
    // Scaffold: no network call. A later slice POSTs this to the leads endpoint.
    setSubmitted(true);
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface-muted p-6">
      <motion.div
        className="w-full max-w-lg"
        initial={reduced ? false : { opacity: 0, y: 16 }}
        animate={reduced ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="mb-6 flex justify-center">
          <Logo className="h-8" />
        </div>

        {submitted ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <span className="rounded-full bg-success-soft px-3 py-1 text-xs font-medium text-success">
                Solicitud recibida
              </span>
              <h1 className="text-xl font-semibold text-text-primary">¡Gracias!</h1>
              <p className="max-w-sm text-sm text-text-muted">
                Hemos recibido tu solicitud. Nuestro equipo la revisará y se pondrá en contacto
                contigo para completar la verificación de documentos y los términos de crédito.
              </p>
              <Link
                to="/"
                className="mt-2 inline-flex h-11 items-center justify-center rounded-md border border-border-strong bg-surface px-4 text-sm font-medium text-text-primary hover:bg-surface-muted"
              >
                Volver al inicio
              </Link>
            </CardContent>
          </Card>
        ) : (
          <>
            <h1 className="text-center text-xl font-semibold text-text-primary">
              Solicitar cuenta
            </h1>
            <p className="mt-1 text-center text-sm text-text-muted">
              Cuéntanos sobre tu empresa. Aprobamos cuentas tras una verificación rápida.
            </p>

            <Card className="mt-6">
              <CardContent className="pt-5">
                <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)} noValidate>
                  <Field label="Tipo de cuenta" htmlFor="accountType" error={errors.accountType?.message}>
                    <select id="accountType" className={inputClass} {...register('accountType')}>
                      <option value="cliente">Cliente</option>
                      <option value="distribuidor">Distribuidor</option>
                    </select>
                  </Field>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Empresa" htmlFor="companyName" error={errors.companyName?.message}>
                      <input id="companyName" className={inputClass} {...register('companyName')} />
                    </Field>
                    <Field label="RIF" htmlFor="rif" error={errors.rif?.message}>
                      <input id="rif" className={inputClass} placeholder="J-12345678-9" {...register('rif')} />
                    </Field>
                  </div>

                  <Field label="Nombre de contacto" htmlFor="contactName" error={errors.contactName?.message}>
                    <input id="contactName" className={inputClass} {...register('contactName')} />
                  </Field>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Correo" htmlFor="email" error={errors.email?.message}>
                      <input id="email" type="email" className={inputClass} {...register('email')} />
                    </Field>
                    <Field label="Teléfono" htmlFor="phone" error={errors.phone?.message}>
                      <input id="phone" className={inputClass} placeholder="+58 ..." {...register('phone')} />
                    </Field>
                  </div>

                  <Field label="Mensaje (opcional)" htmlFor="message" error={errors.message?.message}>
                    <textarea
                      id="message"
                      rows={3}
                      className={`${inputClass} h-auto py-2`}
                      placeholder="Cuéntanos qué productos te interesan…"
                      {...register('message')}
                    />
                  </Field>

                  <Button type="submit" block disabled={isSubmitting}>
                    Enviar solicitud
                  </Button>
                </form>
              </CardContent>
            </Card>

            <p className="mt-6 text-center text-sm text-text-muted">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="font-medium text-brand-primary hover:underline">
                Iniciar sesión
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}

const inputClass =
  'h-11 w-full rounded-md border border-border-strong bg-surface px-3 text-sm text-text-primary placeholder:text-text-muted focus-visible:border-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-text-primary">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
